// TODO: cleanup with research_assistant_gen_3 ff
//
// Extracted from ProjectChatPanel.tsx (OR-6455) so ReadOnlyChatPanel can
// mount the SAME real chat switcher — not a disabled stand-in — without a
// circular import between the two files.

import { Stack, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ActiveChatPicker } from './ActiveChatPicker';
import { useInternalChatVisibilityEnabled } from '../hooks/useInternalChatVisibilityEnabled';
import { clearStickyActiveChat, NEW_CHAT_SENTINEL } from '../hooks/useStickyActiveChat';
import { useActiveChat } from '../lib/api/useProjectChats';

interface ActiveChatHeaderProps {
  projectId: string;
  /**
   * The active chat id, or ``undefined`` in the welcome/empty state. The
   * header (and its switcher) renders in BOTH states so the user can always
   * switch chats or start a new one — OR-6123 F6 dropped the empty-state
   * chats list, so this header is the only switch affordance there.
   */
  chatId: string | undefined;
}

/**
 * Header strip above the active chat. Layout (OR-6123):
 *
 *   "Chat title" ▾
 *
 * - Title ▾ → the {@link ActiveChatPicker} switcher; its label is the current
 *   chat's title, a 'New chat' placeholder until an active-but-unsaved row is
 *   named, or a 'My chats' placeholder (muted when the project has no chats
 *   yet) in the no-chat welcome state. The picker's own dropdown carries the
 *   "+ New chat" action, so no separate button is needed in the header.
 *
 * OR-6455: this switcher is never disabled or omitted when viewing a
 * read-only chat — a viewer must always be able to switch OUT to another
 * chat (their own or another "All" row) without a dead end. It's fully
 * functional here regardless of which chat is currently open; only the
 * BODY below (composer, transcript write affordances) differs between the
 * live and read-only render paths.
 *
 * The private indicator, options kebab (rename/delete), and standalone "New"
 * button were dropped from this header per the latest design — the picker
 * is the sole control here.
 */
export const ActiveChatHeader = ({ projectId, chatId }: ActiveChatHeaderProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const internalVisibilityEnabled = useInternalChatVisibilityEnabled();
  const { chat, isLoading } = useActiveChat({ projectId, chatId, internalVisibilityEnabled });

  const handleNewChat = useCallback(() => {
    // OR-6123: a new chat is EPHEMERAL — it must not persist a DB row until the
    // user's first message. So "+ New chat" shows the no-chatId welcome shell
    // (welcome + composer) rather than minting a ``?chat=<id>`` and eagerly
    // POSTing an empty row. Minting an id without a persisted row would mount
    // ``ActiveChatBody`` and subscribe the socket to a chat that doesn't exist
    // server-side (no-access). The row is born on the first message via the
    // empty-state composer's create-on-submit flow.
    //
    // Navigate to the ``?chat=new`` SENTINEL (not a bare param drop): on a
    // project with live chats, a bare drop falls through ``useEffectiveChatId``'s
    // entry-path resolution and reopens the last-active chat — so "+ New chat"
    // would never reach the welcome shell. The sentinel explicitly signals
    // new-chat intent and suppresses that fallback. Clear sticky too (mirrors
    // the delete-row flow) so nothing else routes the user back.
    clearStickyActiveChat(projectId);
    const next = new URLSearchParams(searchParams);
    next.set('chat', NEW_CHAT_SENTINEL);
    navigate({ search: `?${next.toString()}` });
  }, [navigate, searchParams, projectId]);

  const titleText = chat?.title?.trim() || (isLoading ? '' : chatId ? 'New chat' : 'My chats');

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        flexShrink: 0,
        // Horizontal gutter matches the project page PAGE_GUTTER
        // (theme.spacing(3)) so the chat header aligns with the
        // surrounding gen-3 chrome.
        padding: theme.spacing(1, 3),
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 48,
      }}
      data-testid="active-chat-header"
    >
      <ActiveChatPicker
        projectId={projectId}
        activeChatId={chatId}
        triggerLabel={titleText}
        isParentLoading={isLoading && !chat}
        onNewChat={handleNewChat}
      />
    </Stack>
  );
};
